import { v4 as uuidv4 } from 'uuid'
import { store } from '@remote/store'
import global from '@remote/store/constantStore'
import { getDeviceInfo, checkDeviceMQTTReady } from '../api-http/index'
import { sendXOSMessage as  sendMessage, getCooikeAsync} from '../helper'
import { noop, delay, getErrorMessage } from '@remote/utils/base'
import { isAndroidHezi, isIOSHezi } from '@remote/utils/platform'
import { logInfo, logError } from '@remote/utils/logger'
import type { IMQTTRes } from '../type'
import type { EAppStatus, IDeviceDetail, IDeviceInfo } from '@remote/type/device'
import type { IYunPanFileInfo } from '@remote/type/yunpan'

function extractResult<T = any> (response: IMQTTRes<T>): T {
  const { result } = response
  return result
}

// 设备重启
export function rebootDevice (deviceId: string, cookie: string) {
  return sendMessage('reboot', { device_id: deviceId, cookie })
}

// 设备关机
export function shutdownDevice (deviceId: string, cookie: string) {
  return sendMessage('halt', { device_id: deviceId, cookie })
}

/**
 * 发送指令至设备，升级所有服务版本（包括XOS）, 如果XOS需要升级，会在所有应用升级完成后最后再进行XOS的升级
 * @param mode number 1: 只有XOS需要升级; 2: 只有应用需要升级(XOS无需升级); 3: 应用和XOS都需升级
 */
export async function upgradeAll (mode: number, traceId?: string) {
  const desc = ['', '只有XOS需要升级', '只有应用需要升级(XOS无需升级)', '应用和XOS都需升级']
  logInfo('service_upgrade', {
    traceId,
    action: 'update_all_to_last',
    params: { mode },
    desc: desc[mode],
  })
  return sendMessage('update_all_to_last', { job_id: traceId }).then(extractResult)
}

interface IAppStatusItem {
  app_id: string
  mqtt: 'offline' | 'online'
  status: EAppStatus
  version: number
  launch_mode: {
    type: string
  }
}

interface IGetApplistResult<T> {
  list: T
  version: number
}
export async function getAppListOnDevice<T = IAppStatusItem[]> (traceId = uuidv4()): Promise<IGetApplistResult<T>> {
  // if (!deviceId) return Promise.reject('设备ID异常')
  return sendMessage<{ app_list: T; version?: number }>('app_getstatus', { traceId }).then(extractResult).then(result => {
    const list = Array.isArray(result.app_list) ? result.app_list : ([] as unknown as T)
    const version = result.version || 0
    return { list, version }
  })
}

/** 获取设备一些动态改变的状态数据（如温度、风扇启用情况等） */
export async function getXOSDeviceStatus () {
  return sendMessage<{ data: IDeviceDetail }>('device_getinfo').then(extractResult).then(result => {
    if (!result) throw new Error('设备信息获取异常')
    console.log('device_getinfo', result)
    return result.data
  })
}

type TSetXOSDeviceInfoParams = {
  biz: 'auto_power'| 'alarm' | 'led' | 'lcd' | 'fan' | 'network' // 设置业务类型
  enable?: number // 设置开关：-1 关闭；1 打开
  ext?: Partial<{
    mode: number
    on_time: string
    off_time: string
    repeat: string
  }>
}
/** 设置设备一些动态改变的状态数据（如风扇启用情况、定时开关机等） */
export async function setXOSDeviceInfo (params: TSetXOSDeviceInfoParams) {
  return sendMessage('device_setinfo', {set_config: params})
}
interface IVersionInfo {
  xos: number
  xl_gx: number
  xl_disk: number
  xl_shell: number
  xl_dcdn: number
  xl_thumb: number
  xl_thunder: number
  /** 索引服务 */
  xl_scrape: number
  xl_backer: number
  [app: string]: number
}

/** 获取当前连接设备的XOS版本号 */
interface IXOSVersionInfo {
  version: number
  runtime: string
  publish_type: string
}
type IDeviceDetailBasicInfo = Pick<IDeviceDetail, 'xos' | 'system' | 'profile'>
export async function getXOSVersion (traceId = uuidv4()): Promise<IXOSVersionInfo> {
  return sendMessage<{ data: IDeviceDetailBasicInfo }>('device_getinfo_base', { traceId }).catch((err) => {
    console.error(err)
    logError('xos-mqtt', {
      traceId,
      action: 'device_getinfo_base',
      error: err,
      errorText: getErrorMessage(err),
    })
    // device_getinfo_base为xos_v1119新增方法，可能存在设备为旧版情况而导致版本检测失败，需要降级使用device_getinfo
    return sendMessage<{ data: IDeviceDetail }>('device_getinfo', { traceId })
  }).then(extractResult).then(result => {
    if (!result) throw new Error('设备信息获取异常')
    return result.data
  }).then(detail => {
    return {
      version: +detail.xos.ver,
      runtime: detail.system.runtime,
      publish_type: detail.profile.publish_type,
    }
  })
}

/** 获取当前连接设备的各个服务版本号 */
export async function getXOSServerVersion (traceId = uuidv4()) {
  return getAppListOnDevice(traceId).then(result => {
    const { list: applist, version } = result
    if (!Array.isArray(applist)) throw new Error('设备服务异常')
    const versions = applist.reduce<Omit<IVersionInfo, 'xos'>>((vers, app) => {
      vers[app.app_id] = app.version
      return vers
    }, {})

    return { versions, ver: version }
  })
}

/** 获取当前连接设备所有服务的版本号（xos、xl_gx等），并附带设备环境信息 */
interface IVersionInfoWithENV {
  runtime: string
  publish_type: string
  versions: IVersionInfo
  /** 设备应用版本号 */
  ver: number
}
export async function getAppVersion (traceId = uuidv4()): Promise<IVersionInfoWithENV> {
  return Promise.all([getXOSVersion(traceId), getXOSServerVersion(traceId)]).then((response) => {
    const [xosVersionInfo, versionsWithoutXOS] = response
    const versions: IVersionInfo = ({
      xos: +xosVersionInfo.version,
      ...versionsWithoutXOS.versions,
    } as IVersionInfo)
    const result: IVersionInfoWithENV = {
      runtime: xosVersionInfo.runtime,
      publish_type: xosVersionInfo.publish_type,
      versions,
      ver: versionsWithoutXOS.ver,
    }
    return result
  })
}

interface IDeviceCheckParams {
  traceId?: string
  deviceId: string
  action?: string
}
interface ICheckDeviceOptions {
  leading?: boolean
  timeout?: number
  interval?: number
  targetStatus?: 'online' | 'offline'
  onCheck?: (deviceInfo: IDeviceInfo) => boolean
}
async function pollDeviceStatus (params: IDeviceCheckParams, options: ICheckDeviceOptions) {
  const { deviceId, action = '', traceId = uuidv4() } = params
  const {
    leading = true,
    timeout = 1 * 60 * 1000,
    interval = 5 * 1000,
    targetStatus = 'online',
    onCheck,
  } = options
  function check () {
    return getDeviceInfo(deviceId).then((basicInfo) => {
      if (!basicInfo) throw new Error('get deviceinfo fail.')
      const pollingData = {
        online: basicInfo.online,
        status: basicInfo.status,
        nickname: basicInfo.nickname,
        internal_ip: basicInfo.internal_ip,
      }
      logInfo('xos_polling', { traceId, action, data: pollingData })
      if (onCheck) return onCheck(basicInfo)
      if (basicInfo.online) return targetStatus === 'online'
      return targetStatus === 'offline'
    }).catch((err) => {
      logError('xos_polling', { traceId, action, error: err, errorText: getErrorMessage(err) })
      console.error(err)
      return false
    })
  }

  return new Promise((resolve, reject) => {
    let _interval: any = null
    let timer: any = null
    if (leading) {
      check().then((isOk) => {
        if (isOk) {
          resolve(true)
          timer && clearTimeout(timer)
          _interval && clearInterval(_interval)
        }
      })
    }
    if (timeout) {
      timer = setTimeout(() => {
        reject(new Error('timeout'))
        _interval && clearInterval(_interval)
      }, timeout)
    }
    _interval = setInterval(() => {
      check().then((isOk) => {
        if (isOk) {
          resolve(true)
          timer && clearTimeout(timer)
          _interval && clearInterval(_interval)
        }
      })
    }, interval)
  })
}

interface ICheckServiceOptions {
  timeout?: number
  interval?: number
  leading?: boolean
}
export async function wait4DeviceMQTTOnline (deviceId: string, options: ICheckServiceOptions = {}, traceId = uuidv4()): Promise<boolean> {
  const { timeout = 2 * 60 * 1000, interval = 3 * 1000, leading = false } = options

  return new Promise((resolve, reject) => {
    let _interval: any = null
    let timer: any = null
    if (timeout) {
      timer = setTimeout(() => {
        console.log('[Device]设备应用未正确启动')
        reject(new Error('timeout'))
        _interval && clearInterval(_interval)
      }, timeout)
    }
    const check = () => {
      checkDeviceMQTTReady(deviceId, traceId).then((isOk) => {
        if (isOk) {
          resolve(true)
          timer && clearTimeout(timer)
          _interval && clearInterval(_interval)
        }
      })
    }
    _interval = setInterval(() => check(), interval)
    if (leading) check()
  })
}

type IDetectReOnlineParams = Omit<IUpdateXOSVersionParams, 'onDownloading' | 'onDownloaded' | 'onRebooting'>
export async function DetectXOSReOnline (params: IDetectReOnlineParams) {
  const { deviceId, traceId = uuidv4(), onConnecting = noop } = params
  return pollDeviceStatus({ traceId, deviceId, action: 'update' }, {
    leading: true,
    timeout: 1 * 60 * 1000,
    onCheck: ({ status }) => status === 1,
  }).then(async () => {
    // 重新建立连接
    onConnecting()
    // 等待一分钟，以便设备服务能够充分启动
    // await delay(60 * 1000)
    // 不通过MQTT检测设备应用服务状态
    try {
      await wait4DeviceMQTTOnline(deviceId, {
        timeout: 60 * 1000,
      }, traceId)
    } catch (err) {
      console.log('应用服务未正常启动')
    }

    return true
  })
}

interface IUpdateXOSVersionParams {
  traceId?: string
  deviceId: string
  onDownloading?: () => void
  onDownloaded?: () => void
  onRebooting?: () => void
  onConnecting?: () => void
}
export async function DetectXOSUpgrade (params: IUpdateXOSVersionParams) {
  const { deviceId, traceId = uuidv4(), onDownloaded = noop, onRebooting = noop } = params
  const MAX_TIME = 3 * 60 * 1000
  const MIN_TIME = 30 * 1000
  let isFinished = false
  const cookie = await getCooikeAsync()
  // 通过ping方法检测设备是否离线
  const promise1 = new Promise<void>((resolve, reject) => {
    // pink xos服务、超时时长3秒，连续2次失败则代表设备离线，即达到期望结果
    let timer: number | null = null
    let failedCount = 0
    const startTime = Date.now()
    timer = window.setInterval(() => {
      if (isFinished) {
        resolve()
        if (timer) clearInterval(timer)
        return
      }
      const currTime = Date.now()
      if (currTime - startTime >= MAX_TIME) {
        if (timer) clearInterval(timer)
        console.log('[设备连接]设备MQTT超时未连接')
        reject(new Error('设备未正常重启'))
        return
      }
      sendMessage('ping', { traceId, cookie }, { timeout: 3000 }).then(() => {
        failedCount = 0
      }).catch(() => {
        failedCount += 1
        const isTooFast = currTime - startTime <= MIN_TIME
        if (failedCount >= 2 && !isTooFast) {
          onRebooting()
          resolve()
          if (timer) clearInterval(timer)
        }
      })
    }, 3000)
  })
  // 通过http状态检测设备是否离线，同时更新安装包是否已经下载完成
  const promise2 = pollDeviceStatus({ traceId, deviceId, action: 'update' }, {
    leading: true,
    timeout: MAX_TIME,
    onCheck: ({ status, online }) => {
      if (isFinished) {
        return true
      }
      if (online === false) {
        onRebooting()
        return true
      }
      if (status === 6) {
        onDownloaded()
        return false
      } else if (status === 3 || status === -1) {
        onRebooting()
        return true
      } else if (status === 2) {
        // 有可能因为状态6（安装中）之后查询到的状态直接为2(连接中)，中间没有-1（离线）状态
        onRebooting()
        return true
      }
      return false
    },
    // targetStatus: 'offline',
  })
  await Promise.race([promise1, promise2]).finally(() => {
    isFinished = true
  })

  return DetectXOSReOnline(params)
}

export type TProvider = 'xunlei' | 'baidu' | 'ali'
interface IAuthProviderParams {
  provider: TProvider
  // platform: 'an' | 'ios' | 'pc'
}
interface IAuthProviderResult {
  token_type: 'Bearer'
  /** 访问token，为空时需要客户端重新授权 */
  access_token: string
  /** 授权过期时间 */
  expires_at: string
}
/** 获取认证信息 */
export async function getAuthProvider (params: IAuthProviderParams) {
  const { provider } = params
  const platform = isAndroidHezi ? 'an' : isIOSHezi ? 'ios' : 'pc'
  return sendMessage<IAuthProviderResult>('auth_provider', {
    provider,
    platform,
  }, { native: true }).then(extractResult).then((result: IAuthProviderResult) => {
    if (!result) throw new Error('第三方授权信息获取失败')
    if (!result.access_token) throw new Error('授权过期，请重新授权')
    return result
  })
}

interface IStartAuthProviderParams {
  provider: TProvider
  /** 迅雷传空字符串 */
  code: string
}
export async function startAuthProvider (params: IStartAuthProviderParams) {
  const { provider, code } = params
  const platform = isAndroidHezi ? 'an' : isIOSHezi ? 'ios' : 'pc'
  const userid = +store.getters.userId || +global.userId
  if (!userid) throw new Error('请先登录')
  return sendMessage<null>('authorize', {
    provider,
    code,
    userid,
    platform,
  }, { native: true }).then(extractResult).then(async () => {
    await delay(1000)
    return getAuthProvider({ provider })
  })
}

interface IGetYPFileListParams {
  provider: TProvider
  parent_id: string
  /** 过滤规则 */
  filters?: string
  /**
   * 文件排序规则，默认按名称排序
   *  - name: 按文件名称排序(默认)
   *  - time: 按修改时间排序
   *  - size: 按文件大小排序
   */
  order?: 'name' | 'time' | 'size'
  /** 返回条数，默认返回100条 */
  limit?: number
  /** 偏移量 */
  page_token: string
}
interface IGetYPFileListResult {
  errno: number
  /** 用于加载下一页时指明的偏移量，为空则代表没有下一页数据 */
  next_page_token: string
  /** 文件列表 */
  files: IYunPanFileInfo[]
}
/** 获取第三方云盘列表数据 */
export async function getYunPanFileList (params: IGetYPFileListParams, requestId?:number) {
  const { provider, filters, parent_id, page_token, limit = 20, order = 'name' } = params
  const platform = isAndroidHezi ? 'an' : isIOSHezi ? 'ios' : 'pc'
  return sendMessage<IGetYPFileListResult>('pan_get_file_list', {
    provider,
    parent_id,
    page_token,
    limit,
    order,
    filters,
    platform,
  }, { native: true }).then(extractResult).then((result: IGetYPFileListResult) => {
    if (!result) throw new Error('获取云盘列表出现错误')
    const { errno, next_page_token, files } = result
    const list = Array.isArray(files) ? files : []
    return { errno, next_page_token, files: list, requestId }
  })
}

interface IGetYPFileMetaParams {
  provider: TProvider
  file_id: string
}
/** 获取第三方云盘文件详情 */
export async function getYunPanFileMeta (params: IGetYPFileMetaParams) {
  const { provider, file_id } = params
  const platform = isAndroidHezi ? 'an' : isIOSHezi ? 'ios' : 'pc'
  return sendMessage<Pick<IGetYPFileListResult, 'errno' | 'files'>>('pan_get_file_metas', {
    provider,
    platform,
    file_id,
  }, { native: true }).then(extractResult).then((result: Pick<IGetYPFileListResult, 'errno' | 'files'>) => {
    if (!result) throw new Error('获取云盘文件信息出现错误')
    const { files } = result
    const list = Array.isArray(files) ? files : []
    return list.find(f => f.id === file_id)
  })
}

interface IYunPanReqProxyParams {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  /** 云盘接口URI，如: /drive/v1/files */
  uri: string
  /** 接口请求参数，JSON字符串 */
  reqData: string
}
/** 服务端代理转发云盘请求 */
export async function proxyYunPanRequest (params: IYunPanReqProxyParams) {
  return sendMessage('drive_common', params, { native: true }).then(extractResult).then((result) => {
    if (!result) throw new Error('转发云盘接口时出现错误')
    // console.log('[xos][proxyYunPanRequest]:', params, result)
    return result
  })
}
